/**
 * T1704 — the operator trigger's ONE write call: push an account's agreed
 * destinations to the portal.
 *
 * ⛔ WHY THIS IS ITS OWN MODULE AND NOT AN APPEND TO lib/actions.ts. That file
 * froze at T651 M2 so that sibling milestones filling sibling screens never edit
 * the same declarations; the stated hazard is "two adjacent appends to a shared
 * client file". lib/price-actions.ts, lib/account-actions.ts and
 * lib/destination-actions.ts each record the same ruling. This is the fourth
 * application of it, not a new idea.
 *
 * ⛔ THE COPY COMES FROM THE STATUS, NEVER THE SERVER BODY (SEC-5), matching all
 * three siblings. The route's own sentences are developer-shaped and terse;
 * these are the ones Helen reads mid-call.
 *
 * ⛔ AND IT SENDS NO PRICE AND CARRIES NONE BACK. The payload is one account id;
 * the answer is five counts. The read that sources the projection deliberately
 * selects no figure at all, and this module must never widen either end.
 */
import { ApiError, postJson } from './api.js';

/**
 * The portal's receipt: five counts and nothing else.
 *
 * ⚠️ `-1` IS NOT A COUNT. The server's receipt narrowing emits
 * `intOrNull(...) ?? -1` and records that "0 is a real count while -1 means the
 * portal did not send one" — so a render must test `>= 0` before showing any of
 * these to an operator. Declared here rather than imported from the server
 * module, which pulls in `pg` and would drag a node-only dependency into the
 * browser bundle; the same ruling `parseEuroToCents` records next door.
 */
export type PortalProjectionCounts = {
  accountId: number;
  legacyDeactivated: number;
  upserted: number;
  retired: number;
  activeForAccount: number;
};

/**
 * ⭐ `recorded: false` IS A SUCCESS, AND THAT IS THE HONESTY PROPERTY. The
 * portal WAS updated; only the kiosk-side note of when it happened failed to
 * save. Reporting "nothing was sent" after the far side changed would send
 * Helen to re-send a list that is already correct, and turning it into an error
 * would be worse still.
 */
export type PortalProjectionResult =
  | { ok: true; projection: PortalProjectionCounts; recorded: boolean }
  | { ok: false; message: string };

/**
 * Status to operator copy.
 *
 * ⚠️ TWO OF THIS ROUTE'S REFUSALS ARE UNREACHABLE FROM THIS BUTTON and are
 * deliberately not given wording of their own: the 415 (a non-JSON body) and
 * the 400 that says the account is missing, since the control is rendered
 * INSIDE an account's block and cannot be pressed without one. They fall to the
 * generic line rather than to invented copy for a state that cannot occur.
 * ⛔ The 400 that IS reachable is the empty-book one, so that is what the 400
 * arm says.
 */
function projectionFailure(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 400) {
    return 'This home has no agreed prices yet. Add its prices first.';
  }
  if (status === 409) {
    return 'This home has more agreed prices than one send carries. Tell Alex before sending.';
  }
  if (status === 404) {
    return 'That home is no longer on the list. Reload and try again.';
  }
  if (status === 503) {
    return 'The portal link is switched off, so nothing was sent.';
  }
  return 'Could not send the prices. Try again in a moment.';
}

/**
 * Replace one account's curated destination list in the portal with the places
 * that account is currently priced to go.
 *
 * ⛔ THE ACCOUNT IS THE ONLY THING SENT, and it is the id of the block the
 * button was rendered in. Nothing here searches the price book for it: the
 * caller is already inside the account it means, so the scope is a property of
 * where the control lives rather than of a lookup that could pick wrong.
 */
export async function projectPortalDestinations(
  accountId: number,
): Promise<PortalProjectionResult> {
  try {
    const res = await postJson<{ projection: PortalProjectionCounts; recorded?: boolean }>(
      '/api/portal/destinations', { accountId },
    );
    // An older server that does not report the record at all is treated as
    // having saved it: this flag exists to explain a FAILURE, and inventing one
    // where the server said nothing would be its own false alarm.
    return { ok: true, projection: res.projection, recorded: res.recorded !== false };
  } catch (e) {
    return { ok: false, message: projectionFailure(e) };
  }
}
